import { inject, Injectable } from '@angular/core';
import { AppService, SocketEventType } from '@core';
import { environment } from '@env';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Subject, tap } from 'rxjs';
import { io, Socket } from 'socket.io-client';

import { SocketActions } from './socket.actions';

export const SocketEvents = new Subject<{ data: any; type: SocketEventType; }>();

@Injectable()
export class WebSocketEffects {
  private socket: Socket | null = null;
  private actions$ = inject(Actions);
  private appService = inject(AppService);
  private joinedDeviceId: string | null = null;

  connect$ = createEffect(() => this.actions$.pipe(
    ofType(SocketActions.connect),
    tap(() => this.connect()),
  ), { dispatch: false });

  disconnect$ = createEffect(() => this.actions$.pipe(
    ofType(SocketActions.disconnect),
    tap(() => this.disconnect()),
  ), { dispatch: false });

  reconnect$ = createEffect(() => this.actions$.pipe(
    ofType(SocketActions.reconnet),
    tap(() => {
      this.disconnect();
      this.connect();
    }),
  ), { dispatch: false });

  joinDeviceRoom$ = createEffect(() => this.actions$.pipe(
    ofType(SocketActions.joinDeviceRoom),
    tap(({ deviceId }) => {
      if (this.joinedDeviceId && this.joinedDeviceId !== deviceId) {
        this.socket?.emit('device:leave', this.joinedDeviceId);
      }

      this.joinedDeviceId = deviceId;
      this.socket?.emit('device:join', deviceId);
    }),
  ), { dispatch: false });

  leaveDeviceRoom$ = createEffect(() => this.actions$.pipe(
    ofType(SocketActions.leaveDeviceRoom),
    tap(({ deviceId }) => {
      if (this.joinedDeviceId === deviceId) {
        this.joinedDeviceId = null;
      }

      this.socket?.emit('device:leave', deviceId);
    }),
  ), { dispatch: false });

  private connect() {
    const user = localStorage.getItem('user');
    const accessToken = user && JSON.parse(user)['accessToken'];
    const wsProtocol = location.protocol === 'https:' || location.host.startsWith('localhost')
      ? 'wss:'
      : 'ws:';

    const predefinedWsUrl = (environment as unknown as { wsUrl: string; }).wsUrl;
    const wsUrl = predefinedWsUrl
      ? new URL(predefinedWsUrl).origin
      : `${wsProtocol}//${location.host}`;
    const path = predefinedWsUrl
      ? new URL(predefinedWsUrl).pathname
      : `${environment.api.replace(/\/+$/g, '')}/notifications`;

    this.socket = io(wsUrl, {
      auth: {
        token: accessToken,
      },
      autoConnect: true,
      path,
      reconnection: true,
      reconnectionDelay: 3000,
      transports: ['websocket'],
    });

    this.socket.on('connect', () => {
      this.socket!.emit('client:join', this.appService.currentClient);

      if (this.joinedDeviceId) {
        this.socket!.emit('device:join', this.joinedDeviceId);
      }
    });
    this.socket.onAny((event, data) => SocketEvents.next({ data, type: event }));
  }

  private disconnect() {
    this.socket?.offAny();
    this.socket?.disconnect();
    this.socket = null;
  }
}
